import React, { useEffect, useMemo, useState } from 'react';

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

const SNAP_GRID_PX = 24;

export default function DraggablePivotLayer({
  points = [],
  chartMeta,
  imageWidth = 0,
  imageHeight = 0,
  disabled = false,
  draftPivots = {},
  onDraftChange,
  onDraftCommit,
  snapEnabled = false,
  showGridDuringDrag = false,
}) {
  const [dragState, setDragState] = useState(null);
  const [dragPreview, setDragPreview] = useState(null);

  useEffect(() => {
    setDragState(null);
    setDragPreview(null);
  }, [points, chartMeta, imageWidth, imageHeight, draftPivots]);

  const scale = useMemo(() => {
    const srcW = chartMeta?.w || 1;
    const srcH = chartMeta?.h || 1;
    return {
      x: imageWidth > 0 ? imageWidth / srcW : 1,
      y: imageHeight > 0 ? imageHeight / srcH : 1,
    };
  }, [chartMeta, imageWidth, imageHeight]);

  const shouldShowGrid = showGridDuringDrag && snapEnabled && !!dragState;

  if (!chartMeta || imageWidth <= 0 || imageHeight <= 0 || !points.length) {
    return null;
  }

  const toDisplayX = (point) => {
    if (dragPreview?.index === point.index) return dragPreview.displayX;
    if (draftPivots?.[point.index]?.sourceX !== undefined) {
      return Number(draftPivots[point.index].sourceX) * scale.x;
    }
    return point.x * scale.x;
  };

  const toDisplayY = (point) => {
    if (dragPreview?.index === point.index) return dragPreview.displayY;
    if (draftPivots?.[point.index]?.sourceY !== undefined) {
      return Number(draftPivots[point.index].sourceY) * scale.y;
    }
    return point.y * scale.y;
  };

  const handlePointerDown = (event, point) => {
    if (disabled || point.locked) return;
    event.preventDefault();
    event.stopPropagation();

    const initialDisplayX = toDisplayX(point);
    const initialDisplayY = toDisplayY(point);

    setDragState({
      pointerId: event.pointerId,
      index: point.index,
      label: point.label,
      locked: !!point.locked,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startDisplayX: initialDisplayX,
      startDisplayY: initialDisplayY,
    });

    setDragPreview({
      index: point.index,
      displayX: initialDisplayX,
      displayY: initialDisplayY,
      rawDisplayX: initialDisplayX,
      rawDisplayY: initialDisplayY,
    });

    if (event.currentTarget.setPointerCapture) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };

  const handlePointerMove = (event) => {
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    event.preventDefault();

    const deltaX = event.clientX - dragState.startClientX;
    const deltaY = event.clientY - dragState.startClientY;

    const rawDisplayX = clamp(dragState.startDisplayX + deltaX, 0, imageWidth);
    const rawDisplayY = clamp(dragState.startDisplayY + deltaY, 0, imageHeight);
    const nextDisplayX = rawDisplayX;
    const nextDisplayY = rawDisplayY;

    setDragPreview({
      index: dragState.index,
      displayX: nextDisplayX,
      displayY: nextDisplayY,
      rawDisplayX,
      rawDisplayY,
    });
  };

  const handlePointerUp = (event) => {
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    event.preventDefault();

    const fallbackRawDisplayX = clamp(dragState.startDisplayX, 0, imageWidth);
    const fallbackRawDisplayY = clamp(dragState.startDisplayY, 0, imageHeight);

    const finalRawDisplayX = dragPreview?.index === dragState.index
      ? (dragPreview.rawDisplayX ?? dragPreview.displayX)
      : fallbackRawDisplayX;
    const finalRawDisplayY = dragPreview?.index === dragState.index
      ? (dragPreview.rawDisplayY ?? dragPreview.displayY)
      : fallbackRawDisplayY;

    const sourceX = clamp(finalRawDisplayX / scale.x, 0, chartMeta.w);
    const sourceY = clamp(finalRawDisplayY / scale.y, 0, chartMeta.h);

    const nextDrafts = {
      ...(draftPivots || {}),
      [dragState.index]: {
        sourceX: Number(sourceX.toFixed(4)),
        sourceY: Number(sourceY.toFixed(4)),
        label: dragState.label,
        locked: dragState.locked,
      },
    };

    onDraftChange?.(nextDrafts);
    onDraftCommit?.(nextDrafts, {
      index: dragState.index,
      label: dragState.label,
      locked: dragState.locked,
      sourceX: Number(sourceX.toFixed(4)),
      sourceY: Number(sourceY.toFixed(4)),
    });

    setDragPreview(null);
    setDragState(null);
  };

  const handlePointerCancel = (event) => {
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    event.preventDefault();
    setDragPreview(null);
    setDragState(null);
  };

  return (
    <div className="absolute inset-0 pointer-events-none" data-testid="draggable-pivot-layer">
      {shouldShowGrid && (
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'linear-gradient(to right, rgba(0,245,212,0.17) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,245,212,0.17) 1px, transparent 1px)',
            backgroundSize: `${SNAP_GRID_PX}px ${SNAP_GRID_PX}px`,
          }}
        />
      )}
      {points.map((point) => {
        const x = toDisplayX(point);
        const y = toDisplayY(point);
        const isDragging = dragState?.index === point.index;
        const isDrafted = !isDragging && draftPivots?.[point.index];

        return (
          <button
            key={`${point.index}-${point.label}`}
            type="button"
            className={`absolute -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full border text-[10px] font-mono font-bold pointer-events-auto transition-all ${
              point.locked
                ? 'border-[#f5b400] bg-[#f5b400]/20 text-[#f5b400] cursor-not-allowed'
                : isDragging
                  ? 'border-[#00f5d4] bg-[#00f5d4]/25 text-[#00f5d4] shadow-[0_0_0_3px_rgba(0,245,212,0.2)] cursor-grabbing'
                  : isDrafted
                    ? 'border-[#f5b400] bg-[#f5b400]/20 text-[#f5b400] hover:border-[#f5b400] cursor-grab'
                    : 'border-white/40 bg-black/45 text-white hover:border-[#00f5d4]/70 hover:text-[#00f5d4] cursor-grab'
            }`}
            style={{ left: `${x}px`, top: `${y}px` }}
            onPointerDown={(event) => handlePointerDown(event, point)}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            title={point.locked ? `${point.label} locked` : `Drag ${point.label} freely, then Apply/Rescore`}
            disabled={disabled || point.locked}
          >
            {point.label}
          </button>
        );
      })}
    </div>
  );
}



